import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { OrdersService } from '../../../order/order.service';
import { SalesOrderLine } from '../../../order/sales-order/sales-order-line.model';
import { SalesOrderConversionWarning, SalesOrderFromQuotationPreview, SalesOrderFromQuotationRequest } from '../../../order/sales-order/sales-order-quotation-conversion.model';
import { PaymentTerm } from '../../../order/models/payment-term.model';
import { Customer } from '../../../customer/customer.model';
import { CustomerService } from '../../../customer/customer.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';

@Component({
  selector: 'app-quotation-sales-order-conversion',
  standalone: true,
  imports: [
    LogisticsProjectLookupComponent,
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './quotation-sales-order-conversion.component.html',
  styleUrls: ['./quotation-sales-order-conversion.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuotationSalesOrderConversionComponent implements OnInit {
  quotationId?: number;
  companyId = 0;
  loading = signal(false);
  converting = signal(false);
  preview = signal<SalesOrderFromQuotationPreview | null>(null);
  selectedLineIds = signal<number[]>([]);
  selectedInstallationRowIds = signal<number[]>([]);
  paymentTerms: PaymentTerm[] = [];
  customers: Customer[] = [];

  request: SalesOrderFromQuotationRequest = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ordersService: OrdersService,
    private customerService: CustomerService,
    private sessionStorageService: SessionStorageService,
    private toolbarService: ToolbarService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isFinite(id)) {
      return;
    }
    this.quotationId = id;
    this.companyId = this.sessionStorageService.getCompany()?.id || this.toolbarService.customer()?.company?.id || 0;
    this.request.actorId = this.toolbarService.customer()?.id;
    this.request.logisticsProjectId = Number(this.route.snapshot.queryParamMap.get('projectId') || '') || undefined;
    this.loadReferenceData();
    this.loadPreview();
  }

  loadReferenceData(): void {
    this.ordersService.getAllPaymentTerms().subscribe(terms => this.paymentTerms = terms || []);
    if (this.companyId) {
      this.customerService.getCustomersByCompany(this.companyId).subscribe(customers => this.customers = customers || []);
    }
  }

  loadPreview(): void {
    if (!this.quotationId || !this.companyId) return;
    this.loading.set(true);
    this.ordersService.previewSalesOrderFromQuotation(this.quotationId, this.companyId, this.request).subscribe({
      next: preview => {
        this.preview.set(preview);
        this.request.customerId = this.request.customerId || preview.targetCustomer?.id || preview.quotation?.customerId;
        this.request.expectedDate = this.request.expectedDate || preview.quotation?.expiryDate;
        const lineIds = (preview.proposedLines || [])
          .map(line => line.sourceQuotationLineId)
          .filter((lineId): lineId is number => !!lineId);
        const installationRowIds = (preview.proposedLines || [])
          .map(line => line.sourceQuotationInstallationRowId)
          .filter((rowId): rowId is number => !!rowId);
        if (!this.selectedLineIds().length && !this.selectedInstallationRowIds().length) {
          this.selectedLineIds.set(lineIds);
          this.selectedInstallationRowIds.set(installationRowIds);
          this.request.selectedLineIds = lineIds;
          this.request.selectedInstallationRowIds = installationRowIds;
        }
        this.loading.set(false);
      },
      error: error => {
        this.loading.set(false);
        this.messageService.add({ severity: 'error', summary: 'Preview failed', detail: error?.error?.message || 'Could not load sales order preview.' });
      }
    });
  }

  convert(): void {
    if (this.converting()) {
      return;
    }
    if (!this.quotationId || !this.companyId || !this.preview()?.conversionAllowed) {
      this.messageService.add({ severity: 'warn', summary: 'Not ready', detail: 'Resolve blocking warnings before conversion.' });
      return;
    }
    this.converting.set(true);
    this.ordersService.createSalesOrderFromQuotation(this.quotationId, this.companyId, {
      ...this.request,
      selectedLineIds: this.selectedLineIds(),
      selectedInstallationRowIds: this.selectedInstallationRowIds()
    }).subscribe({
      next: result => {
        this.converting.set(false);
        const orderId = result.salesOrder?.orderID;
        this.messageService.add({ severity: 'success', summary: 'Sales order created', detail: result.salesOrder?.orderNumber || 'Quotation converted.' });
        if (orderId) {
          this.router.navigate(['/order/sales-order/view', orderId]);
        }
      },
      error: error => {
        this.converting.set(false);
        this.messageService.add({ severity: 'error', summary: 'Conversion failed', detail: error?.error?.message || error?.error || 'Could not create sales order.' });
      }
    });
  }

  toggleLine(line: SalesOrderLine, checked?: boolean): void {
    if (line.sourceQuotationInstallationRowId) {
      const current = new Set(this.selectedInstallationRowIds());
      checked ? current.add(line.sourceQuotationInstallationRowId) : current.delete(line.sourceQuotationInstallationRowId);
      this.selectedInstallationRowIds.set(Array.from(current));
      this.request.selectedInstallationRowIds = this.selectedInstallationRowIds();
      return;
    }
    if (!line.sourceQuotationLineId) return;
    const current = new Set(this.selectedLineIds());
    checked ? current.add(line.sourceQuotationLineId) : current.delete(line.sourceQuotationLineId);
    this.selectedLineIds.set(Array.from(current));
    this.request.selectedLineIds = this.selectedLineIds();
  }

  isLineSelected(line: SalesOrderLine): boolean {
    if (line.sourceQuotationInstallationRowId) {
      return this.selectedInstallationRowIds().includes(line.sourceQuotationInstallationRowId);
    }
    return !!line.sourceQuotationLineId && this.selectedLineIds().includes(line.sourceQuotationLineId);
  }

  selectedCount(): number {
    return this.selectedLineIds().length + this.selectedInstallationRowIds().length;
  }

  lineSourceLabel(line: SalesOrderLine): string {
    if (line.sourceQuotationInstallationRowId) {
      return `Installation row ${line.sourceQuotationInstallationRowId}`;
    }
    return `Quotation line ${line.sourceQuotationLineId || '-'}`;
  }

  warningSeverity(warning: SalesOrderConversionWarning): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    return warning.severity === 'ERROR' ? 'danger' : warning.severity === 'WARN' ? 'warn' : 'info';
  }

  isReady(): boolean {
    return !!this.preview()?.conversionAllowed && !!this.request.paymentTermId && this.selectedCount() > 0;
  }
}
